import { lazy, type ComponentType } from 'react'

import { SessionKeys, sessionService } from '@/utils/localStorageService'

/**
 * ЛЕНИВЫЙ МАРШРУТ, КОТОРЫЙ ПЕРЕЖИВАЕТ ПЕРЕСБОРКУ.
 *
 * Замеренный сбой 2026-09-22, воронка 3: «после реги белый экран». Человек открыл лендинг на одной
 * сборке, прошёл онбординг, а пока он заполнял форму, `dist/` пересобрали. Нажатие «Open my
 * workspace» — переход без перезагрузки, и вкладка запросила чанк `AppWizard-<старый хеш>.js`,
 * которого после сборки уже не было. Сервис отвечал на него index.html, браузер отверг HTML как
 * модуль, `React.lazy` бросил, а ловить было некому — корень размонтировался, на экране 0 символов.
 *
 * Это не стендовая болезнь. Каждый деплой (`npm run build` → рестарт) делает то же самое с каждым,
 * кто в этот момент посреди воронки, — и ровно на самом дорогом шаге: сразу после события Lead.
 *
 * Лечение — перезагрузить страницу ОДИН раз. index.html приходит без кеша (server/src/index.ts),
 * поэтому перезагрузка приносит новые имена чанков, а всё, что человек успел ввести, уже лежит в
 * хранилище: спека и состояние визарда пишутся на передаче из онбординга, до перехода.
 *
 * ОДИН ЛЕЧЕБНЫЙ ЗАХОД НА ЗДОРОВУЮ СБОРКУ — счётчиком, а не окном времени. Первая версия запрещала
 * вторую перезагрузку в течение 30 секунд, и ревью показало дыру: если сама перезагрузка шла дольше
 * окна (медленная сеть, тяжёлая страница), настоящая поломка перезагружала вкладку по кругу. Теперь
 * метка ставится перед перезагрузкой и снимается только УСПЕШНОЙ загрузкой любого ленивого маршрута
 * (`markRouteLoaded`): пропажа после перезагрузки — это уже не старая вкладка, а поломка, и она идёт в
 * экран сбоя (AppErrorBoundary), сколько бы времени ни прошло. Следующий деплой в той же сессии
 * снова получит свою одну перезагрузку — к тому моменту метку уже сняла здоровая загрузка.
 *
 * `vite:preloadError` здесь НЕ СЛУШАЕТСЯ, и это тоже урок ревью. Первая версия ловила это событие в
 * main.tsx и вызывала `preventDefault`, а по контракту Vite это значит «ошибку не бросать»: обещание
 * загрузки разрешалось `undefined`, наш `.catch` не срабатывал, React читал `undefined.default`, и
 * старая страница на время перезагрузки показывала экран сбоя и отправляла ложный `app_crashed`.
 * Без `preventDefault` Vite бросает ошибку предзагрузки (в том числе «Unable to preload CSS») прямо
 * в обещание импорта — сюда, где она и лечится.
 */

/**
 * Узнаём сбой загрузки чанка по тексту, потому что у него нет типа: браузеры бросают TypeError с
 * СВОЕЙ формулировкой. Список — по движкам, как их пишут на сегодня.
 */
const STALE_CHUNK = [
  'Failed to fetch dynamically imported module', // Chrome, Edge
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // Safari
  'Unable to preload CSS', // vite: предзагрузка css соседнего чанка
  'Expected a JavaScript', // MIME-отказ, когда вместо модуля пришёл HTML
]

export const isStaleChunkError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '')
  return STALE_CHUNK.some((fragment) => message.includes(fragment))
}

/**
 * Эта страница уже уходит на перезагрузку. Флаг живёт в памяти документа, а не в хранилище: он нужен
 * ровно до его выгрузки. Пока он стоит, любая следующая пропажа (соседний css, второй маршрут)
 * ждёт вместе с первой, а не считается «второй подряд», и экран сбоя не рисуется — за доли секунды до
 * перезагрузки он был бы вспышкой неправды о поломке, которая уже лечится.
 */
let reloading = false

export const isReloading = () => reloading

/**
 * Перезагрузить, если с последней здоровой загрузки маршрута этого ещё не делали. `true` —
 * перезагрузка запущена (или уже идёт), вызывающему остаётся ждать; `false` — лечебный заход уже
 * потрачен или хранилище недоступно, ошибку надо показать.
 */
export const reloadOnceForStaleChunk = (): boolean => {
  if (reloading) return true
  if (sessionService.getItem(SessionKeys.chunkReload)) return false
  sessionService.setItem(SessionKeys.chunkReload, '1')
  /* Замок держится на sessionStorage, и там, где его нет (приватный режим старого Safari, запрет
     хранилища), метка молча не пишется — а без метки настоящая поломка сборки перезагружала бы
     вкладку по кругу. Поэтому перезагружаемся только если метка ПРОЧИТАЛАСЬ обратно; иначе ошибка
     идёт в экран сбоя, где кнопка перезагрузки — решение человека, а не петля. */
  if (sessionService.getItem(SessionKeys.chunkReload) !== '1') return false
  sessionService.setItem(SessionKeys.chunkReloaded, '1')
  reloading = true
  window.location.reload()
  return true
}

/** Маршрут загрузился — сборка здоровая, следующий деплой снова заслуживает одну перезагрузку. */
const markRouteLoaded = () => {
  if (sessionService.getItem(SessionKeys.chunkReload)) sessionService.removeItem(SessionKeys.chunkReload)
}

const never = <T,>() => new Promise<T>(() => {})

/**
 * `React.lazy` с одним лекарством: пропавший чанк → одна перезагрузка. Пока страница уходит на
 * перезагрузку, обещание не разрешается никогда — тогда переход (react-router ведёт его в
 * startTransition) не фиксируется на старой странице: ни экрана сбоя, ни второго page_viewed.
 *
 * Пустой модуль — тоже не повод рисовать: так выглядит загрузка, у которой ошибку съел чужой
 * `preventDefault`. Во время перезагрузки ждём, иначе это настоящая поломка и она идёт в границу.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- та же граница типа, что у самого React.lazy
export const lazyRoute = <T extends ComponentType<any>>(load: () => Promise<{ default: T }>) =>
  lazy<T>(() =>
    load().then(
      (module): Promise<{ default: T }> | { default: T } => {
        if (module?.default) {
          markRouteLoaded()
          return module
        }
        if (reloading) return never()
        return Promise.reject(new Error('a lazy route resolved to an empty module'))
      },
      (error: unknown): Promise<{ default: T }> => {
        if (isStaleChunkError(error) && reloadOnceForStaleChunk()) return never()
        throw error
      },
    ),
  )

/**
 * Перезагрузка из-за пропавшего чанка случилась — отдать это событие уже с новой страницы. Отправлять
 * его ДО перезагрузки нельзя: батч аналитики умер бы вместе со старой страницей, и у нас не было бы
 * числа, сколько людей деплой вытолкнул из воронки.
 */
export const consumeStaleChunkReload = (): boolean => {
  const happened = sessionService.getItem(SessionKeys.chunkReloaded) === '1'
  if (happened) sessionService.removeItem(SessionKeys.chunkReloaded)
  return happened
}
